import { Controller } from "@hotwired/stimulus"

// Field configuration per asset type
const ASSET_TYPE_CONFIG = {
  SAFE:   { quantityLabel: "Amount ($)", showCap: true,  showDiscount: true },
  NOTE:   { quantityLabel: "Principal ($)", showCap: true,  showDiscount: true },
  EQUITY: { quantityLabel: "Shares", showCap: false, showDiscount: false },
  CASH:   { quantityLabel: "Amount ($)", showCap: false, showDiscount: false },
  ESCROW: { quantityLabel: "Amount ($)", showCap: false, showDiscount: false },
}

// Dollar-denominated types: quantity IS the dollar value, price is always $1.00
// Only EQUITY has a meaningful variable price per unit (shares).
const DOLLAR_DENOMINATED_TYPES = new Set(["SAFE", "NOTE", "CASH", "ESCROW"])

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

export default class extends Controller {
  static targets = [
    "existingEntityTab",
    "newEntityTab",
    "existingEntityPanel",
    "newEntityPanel",
    "entityIdField",
    "entitySearch",
    "entitySearchContainer",
    "entityRow",
    "selectedEntityName",
    "selectedEntityBanner",
    "investedCapital",
    "assetsList",
    "assetRow",
    "removeAssetButton",
    "markSummary",
    "summaryInvested",
    "summaryTotalValue",
    "summaryTotalStatus",
    "summaryAssetCount",
    "submitButton",
    "addNoteTab",
    "existingNotesTab",
    "addNotePanel",
    "existingNotesPanel",
    "existingNotesCount",
    "existingNotesList",
    "locationRows",
    "locationRow"
  ]

  static values = {
    locations: { type: Array, default: [] }
  }

  connect() {
    this.lockedAssets = new Set() // Track which EQUITY rows have locked mark values

    // Restore UI state on re-render (e.g. after failed form submission)
    this._restoreEntityState()
    this._restoreAssetRowFields()

    // Close location dropdowns on outside click or Escape
    this._boundCloseDropdowns = (e) => this._handleDocumentClick(e)
    this._boundEscapeDropdowns = (e) => { if (e.key === "Escape") this._closeAllLocationDropdowns() }
    document.addEventListener("click", this._boundCloseDropdowns)
    document.addEventListener("keydown", this._boundEscapeDropdowns)

    this._updateRemoveButtonVisibility()
    this.recalculate()
  }

  disconnect() {
    document.removeEventListener("click", this._boundCloseDropdowns)
    document.removeEventListener("keydown", this._boundEscapeDropdowns)
  }

  // Restore entity tab and selection from the hidden field value
  _restoreEntityState() {
    const entityId = this.entityIdFieldTarget.value

    if (entityId === "new") {
      // "Create New" tab was active
      this.showNewTab()
    } else if (entityId && entityId !== "") {
      // An existing entity was selected — show existing tab and highlight the row
      this.showExistingTab()
      const matchingRow = this.entityRowTargets.find(row => row.dataset.entityId === entityId)
      if (matchingRow) {
        matchingRow.classList.add("bg-primary-50", "dark:bg-primary-900/20")
        matchingRow.classList.remove("hover:bg-gray-50", "dark:hover:bg-gray-700")
        // Show the selected banner and hide search/table
        this.selectedEntityNameTarget.textContent = matchingRow.querySelector(".font-medium")?.textContent || ""
        this.selectedEntityBannerTarget.classList.remove("hidden")
        if (this.hasEntitySearchContainerTarget) {
          this.entitySearchContainerTarget.classList.add("hidden")
        }
        // Fetch existing notes for the restored entity
        this._fetchEntityNotes(entityId)
      }
    } else {
      this.showExistingTab()
    }
  }

  // Apply asset-type-specific field configs for any pre-selected asset types on re-rendered rows
  _restoreAssetRowFields() {
    this.assetRowTargets.forEach(row => {
      const assetType = this._getRowAssetType(row)
      if (assetType) {
        this._updateAssetRowFields(row, assetType)
      }
    })
  }

  // --- Entity notes fetching ---

  _fetchEntityNotes(entityId) {
    if (!this.hasExistingNotesListTarget) return

    // Derive the notes URL from the form action (e.g., /funds/123/positions -> /funds/123/positions/entity_notes)
    const formAction = this.element.getAttribute("action")
    const notesUrl = `${formAction}/entity_notes?entity_id=${entityId}`

    this.existingNotesListTarget.innerHTML = '<div class="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">Loading notes...</div>'

    fetch(notesUrl, {
      headers: {
        "Accept": "text/html",
        "X-Requested-With": "XMLHttpRequest"
      }
    })
      .then(response => {
        if (response.ok) return response.text()
        throw new Error("Failed to load notes")
      })
      .then(html => {
        this.existingNotesListTarget.innerHTML = html
        // Update the notes count badge
        this._updateNotesCount()
      })
      .catch(() => {
        this.existingNotesListTarget.innerHTML = '<div class="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">Could not load notes.</div>'
        this._updateNotesCount()
      })
  }

  _hideEntityNotes() {
    if (!this.hasExistingNotesListTarget) return
    this.existingNotesListTarget.innerHTML = '<div class="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">Select a company to see its notes.</div>'
    this._updateNotesCount()
  }

  _updateNotesCount() {
    if (!this.hasExistingNotesCountTarget) return
    // Count the number of note items (divs with data attributes or specific structure)
    const noteItems = this.existingNotesListTarget.querySelectorAll("[data-note-id]")
    const count = noteItems.length
    this.existingNotesCountTarget.textContent = count > 0 ? count.toString() : ""
  }

  // --- Notes tab switching ---

  showAddNoteTab() {
    this.addNoteTabTarget.classList.add("text-primary-600", "border-primary-600", "dark:text-primary-500", "dark:border-primary-500")
    this.addNoteTabTarget.classList.remove("text-gray-500", "border-transparent", "dark:text-gray-400")
    this.existingNotesTabTarget.classList.remove("text-primary-600", "border-primary-600", "dark:text-primary-500", "dark:border-primary-500")
    this.existingNotesTabTarget.classList.add("text-gray-500", "border-transparent", "dark:text-gray-400")

    this.addNotePanelTarget.classList.remove("hidden")
    this.existingNotesPanelTarget.classList.add("hidden")
  }

  showExistingNotesTab() {
    this.existingNotesTabTarget.classList.add("text-primary-600", "border-primary-600", "dark:text-primary-500", "dark:border-primary-500")
    this.existingNotesTabTarget.classList.remove("text-gray-500", "border-transparent", "dark:text-gray-400")
    this.addNoteTabTarget.classList.remove("text-primary-600", "border-primary-600", "dark:text-primary-500", "dark:border-primary-500")
    this.addNoteTabTarget.classList.add("text-gray-500", "border-transparent", "dark:text-gray-400")

    this.existingNotesPanelTarget.classList.remove("hidden")
    this.addNotePanelTarget.classList.add("hidden")
  }

  // --- Location rows (add/remove/autocomplete) ---

  addLocationRow() {
    if (!this.hasLocationRowsTarget) return
    const index = new Date().getTime()
    const inputClass = "portal-bg-subtle border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2 dark:border-gray-600 dark:text-white"

    const row = document.createElement("div")
    row.className = "flex items-center gap-2"
    row.setAttribute("data-position-form-target", "locationRow")
    row.innerHTML = `
      <div class="relative flex-1">
        <input type="text"
               name="position[new_entity][locations_attributes][${index}][city]"
               placeholder="City"
               autocomplete="off"
               data-action="input->position-form#locationFieldInput focus->position-form#locationFieldFocused"
               class="${inputClass}"
               data-field="location-city">
      </div>
      <div class="relative flex-1">
        <input type="text"
               name="position[new_entity][locations_attributes][${index}][region]"
               placeholder="State / Region"
               autocomplete="off"
               data-action="input->position-form#locationFieldInput focus->position-form#locationFieldFocused"
               class="${inputClass}"
               data-field="location-region">
      </div>
      <div class="relative flex-1">
        <input type="text"
               name="position[new_entity][locations_attributes][${index}][country]"
               placeholder="Country"
               autocomplete="off"
               data-action="input->position-form#locationFieldInput focus->position-form#locationFieldFocused"
               class="${inputClass}"
               data-field="location-country">
      </div>
      <button type="button"
              class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 flex-shrink-0 text-sm"
              data-action="click->position-form#removeLocationRow">
        Remove
      </button>
    `
    this.locationRowsTarget.appendChild(row)
  }

  removeLocationRow(event) {
    const row = event.target.closest("[data-position-form-target='locationRow']")
    if (row) row.remove()
  }

  // --- Location autocomplete dropdown ---

  locationFieldInput(event) {
    this._showLocationDropdown(event.target)
  }

  locationFieldFocused(event) {
    this._showLocationDropdown(event.target)
  }

  _handleDocumentClick(event) {
    // Close dropdowns if click is outside any location input or dropdown
    if (!event.target.closest(".location-autocomplete-dropdown") &&
        !event.target.matches("[data-field^='location-']")) {
      this._closeAllLocationDropdowns()
    }
  }

  _closeAllLocationDropdowns() {
    this.element.querySelectorAll(".location-autocomplete-dropdown").forEach(d => d.remove())
  }

  _showLocationDropdown(input) {
    const fieldType = input.dataset.field // "location-city", "location-region", "location-country"
    const query = input.value.trim().toLowerCase()
    const wrapper = input.closest(".relative")

    // Remove any existing dropdown on this wrapper
    const existing = wrapper.querySelector(".location-autocomplete-dropdown")
    if (existing) existing.remove()

    const suggestions = this._getGroupedSuggestions(query, fieldType, input)
    if (suggestions.length === 0) return

    const dropdown = document.createElement("div")
    dropdown.className = "location-autocomplete-dropdown absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg dark:bg-gray-800 dark:border-gray-700 overflow-y-auto"
    dropdown.style.maxHeight = "240px"

    suggestions.forEach(group => {
      // Country group header
      const header = document.createElement("div")
      header.className = "px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400 sticky top-0"
      header.textContent = group.country
      dropdown.appendChild(header)

      group.items.forEach(item => {
        const option = document.createElement("button")
        option.type = "button"
        option.className = "w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-gray-600 cursor-pointer"
        option.textContent = item.label
        option.addEventListener("click", () => {
          this._selectLocationSuggestion(input, fieldType, item)
          dropdown.remove()
        })
        dropdown.appendChild(option)
      })
    })

    wrapper.appendChild(dropdown)
  }

  _getGroupedSuggestions(query, fieldType, input) {
    const locations = this.locationsValue
    if (!locations || locations.length === 0) return []

    const row = input.closest("[data-position-form-target='locationRow']")

    let filtered
    if (fieldType === "location-city") {
      filtered = locations.filter(loc =>
        loc.city && (query === "" || loc.city.toLowerCase().includes(query))
      )
    } else if (fieldType === "location-region") {
      // Also narrow by country if already filled
      const countryInput = row.querySelector("[data-field='location-country']")
      const countryVal = countryInput ? countryInput.value.trim().toLowerCase() : ""
      filtered = locations.filter(loc =>
        loc.region && (query === "" || loc.region.toLowerCase().includes(query)) &&
        (!countryVal || !loc.country || loc.country.toLowerCase().includes(countryVal))
      )
    } else if (fieldType === "location-country") {
      filtered = locations.filter(loc =>
        loc.country && (query === "" || loc.country.toLowerCase().includes(query))
      )
    } else {
      return []
    }

    // Deduplicate and build display items
    const seen = new Set()
    const items = []
    filtered.forEach(loc => {
      let key, label
      if (fieldType === "location-city") {
        key = `${(loc.city || "").toLowerCase()}|${(loc.region || "").toLowerCase()}|${(loc.country || "").toLowerCase()}`
        label = [loc.city, loc.region].filter(Boolean).join(", ")
      } else if (fieldType === "location-region") {
        key = `${(loc.region || "").toLowerCase()}|${(loc.country || "").toLowerCase()}`
        label = loc.region
      } else {
        key = (loc.country || "").toLowerCase()
        label = loc.country
      }
      if (!seen.has(key)) {
        seen.add(key)
        items.push({ ...loc, label, _key: key })
      }
    })

    // Group by country
    const groups = {}
    items.forEach(item => {
      const country = item.country || "Other"
      if (!groups[country]) groups[country] = []
      groups[country].push(item)
    })

    // Sort items within each group alphabetically
    Object.values(groups).forEach(arr => arr.sort((a, b) => a.label.localeCompare(b.label)))

    // Sort groups: US-like countries first, then alphabetical
    const US_KEYS = new Set(["US", "USA", "United States", "United States of America"])
    const sortedCountries = Object.keys(groups).sort((a, b) => {
      const aIsUS = US_KEYS.has(a)
      const bIsUS = US_KEYS.has(b)
      if (aIsUS && !bIsUS) return -1
      if (!aIsUS && bIsUS) return 1
      return a.localeCompare(b)
    })

    // Limit total suggestions to avoid massive lists
    return sortedCountries.map(country => ({
      country,
      items: groups[country]
    }))
  }

  _selectLocationSuggestion(input, fieldType, item) {
    const row = input.closest("[data-position-form-target='locationRow']")
    if (!row) return

    if (fieldType === "location-city") {
      input.value = item.city || ""
      const regionInput = row.querySelector("[data-field='location-region']")
      const countryInput = row.querySelector("[data-field='location-country']")
      if (regionInput && item.region) regionInput.value = item.region
      if (countryInput && item.country) countryInput.value = item.country
    } else if (fieldType === "location-region") {
      input.value = item.region || ""
      const countryInput = row.querySelector("[data-field='location-country']")
      if (countryInput && item.country) countryInput.value = item.country
    } else if (fieldType === "location-country") {
      input.value = item.country || ""
    }
  }

  // --- Tab switching ---

  showExistingTab() {
    this.existingEntityTabTarget.classList.add("text-primary-600", "border-primary-600", "dark:text-primary-500", "dark:border-primary-500")
    this.existingEntityTabTarget.classList.remove("text-gray-500", "border-transparent", "dark:text-gray-400")
    this.newEntityTabTarget.classList.remove("text-primary-600", "border-primary-600", "dark:text-primary-500", "dark:border-primary-500")
    this.newEntityTabTarget.classList.add("text-gray-500", "border-transparent", "dark:text-gray-400")

    this.existingEntityPanelTarget.classList.remove("hidden")
    this.newEntityPanelTarget.classList.add("hidden")
  }

  showNewTab() {
    this.newEntityTabTarget.classList.add("text-primary-600", "border-primary-600", "dark:text-primary-500", "dark:border-primary-500")
    this.newEntityTabTarget.classList.remove("text-gray-500", "border-transparent", "dark:text-gray-400")
    this.existingEntityTabTarget.classList.remove("text-primary-600", "border-primary-600", "dark:text-primary-500", "dark:border-primary-500")
    this.existingEntityTabTarget.classList.add("text-gray-500", "border-transparent", "dark:text-gray-400")

    this.newEntityPanelTarget.classList.remove("hidden")
    this.existingEntityPanelTarget.classList.add("hidden")

    // Clear existing entity selection when switching to new
    this.entityIdFieldTarget.value = "new"
    this.deselectAllRows()
    this._hideEntityNotes()
  }

  // --- Entity search / filter ---

  filterEntities() {
    const query = this.entitySearchTarget.value.toLowerCase().trim()

    this.entityRowTargets.forEach(row => {
      const name = row.dataset.entityName || ""
      const website = row.dataset.entityWebsite || ""
      const match = name.includes(query) || website.includes(query)
      row.style.display = match ? "" : "none"
    })
  }

  // --- Entity selection ---

  selectEntity(event) {
    const row = event.currentTarget
    const entityId = row.dataset.entityId
    const entityName = row.dataset.entityName

    // Update hidden field
    this.entityIdFieldTarget.value = entityId

    // Highlight selected row
    this.deselectAllRows()
    row.classList.add("bg-primary-50", "dark:bg-primary-900/20")
    row.classList.remove("hover:bg-gray-50", "dark:hover:bg-gray-700")

    // Show selected banner and hide search/table
    this.selectedEntityNameTarget.textContent = entityName
    this.selectedEntityBannerTarget.classList.remove("hidden")
    if (this.hasEntitySearchContainerTarget) {
      this.entitySearchContainerTarget.classList.add("hidden")
    }

    // Fetch and display existing notes
    this._fetchEntityNotes(entityId)
  }

  deselectAllRows() {
    this.entityRowTargets.forEach(row => {
      row.classList.remove("bg-primary-50", "dark:bg-primary-900/20")
      row.classList.add("hover:bg-gray-50", "dark:hover:bg-gray-700")
    })
    this.selectedEntityBannerTarget.classList.add("hidden")
  }

  clearEntitySelection() {
    this.entityIdFieldTarget.value = ""
    this.deselectAllRows()
    this._hideEntityNotes()
    // Show the search/table again
    if (this.hasEntitySearchContainerTarget) {
      this.entitySearchContainerTarget.classList.remove("hidden")
    }
  }

  // --- Dynamic asset fields based on type ---

  assetTypeChanged(event) {
    const row = event.target.closest("tr")
    const assetType = event.target.value
    this._updateAssetRowFields(row, assetType)
    this.recalculate()
  }

  _updateAssetRowFields(row, assetType) {
    const config = ASSET_TYPE_CONFIG[assetType]
    const capCell = row.querySelector("[data-field='cap']")
    const discountCell = row.querySelector("[data-field='discount']")
    const quantityLabel = row.querySelector("[data-field='quantity-label']")
    const lockButton = row.querySelector("[data-field='lock-button']")
    const markPriceInput = row.querySelector("[data-field='mark-price']")

    if (config) {
      // Update quantity label
      if (quantityLabel) {
        quantityLabel.textContent = config.quantityLabel
      }

      // Toggle cap field
      if (capCell) {
        const input = capCell.querySelector("input")
        if (config.showCap) {
          capCell.classList.remove("opacity-40", "pointer-events-none")
          if (input) input.disabled = false
        } else {
          capCell.classList.add("opacity-40", "pointer-events-none")
          if (input) { input.disabled = true; input.value = "" }
        }
      }

      // Toggle discount field
      if (discountCell) {
        const input = discountCell.querySelector("input")
        if (config.showDiscount) {
          discountCell.classList.remove("opacity-40", "pointer-events-none")
          if (input) input.disabled = false
        } else {
          discountCell.classList.add("opacity-40", "pointer-events-none")
          if (input) { input.disabled = true; input.value = "" }
        }
      }

      // For dollar-denominated types: disable price input, hide lock button
      // For EQUITY: enable price input, show lock button
      const isDollar = DOLLAR_DENOMINATED_TYPES.has(assetType)

      if (markPriceInput) {
        if (isDollar) {
          markPriceInput.disabled = true
          markPriceInput.value = ""
          markPriceInput.placeholder = "$1.00"
        } else {
          markPriceInput.disabled = false
          markPriceInput.placeholder = "Auto"
        }
      }

      if (lockButton) {
        if (isDollar) {
          lockButton.classList.add("hidden")
          const rowId = row.dataset.rowId
          if (rowId && this.lockedAssets.has(rowId)) {
            this.lockedAssets.delete(rowId)
          }
        } else {
          lockButton.classList.remove("hidden")
        }
      }
    } else {
      // No type selected - show all fields enabled, show lock button
      if (capCell) {
        capCell.classList.remove("opacity-40", "pointer-events-none")
        const input = capCell.querySelector("input")
        if (input) input.disabled = false
      }
      if (discountCell) {
        discountCell.classList.remove("opacity-40", "pointer-events-none")
        const input = discountCell.querySelector("input")
        if (input) input.disabled = false
      }
      if (quantityLabel) {
        quantityLabel.textContent = "Quantity"
      }
      if (lockButton) {
        lockButton.classList.remove("hidden")
      }
      if (markPriceInput) {
        markPriceInput.disabled = false
        markPriceInput.placeholder = "Auto"
      }
    }
  }

  // --- Lock/Unlock mark value (only for EQUITY) ---

  toggleLock(event) {
    const row = event.target.closest("tr")
    const rowId = row.dataset.rowId

    if (this.lockedAssets.has(rowId)) {
      this._unlockRow(row, rowId)
    } else {
      this._lockRow(row, rowId)
    }

    this.recalculate()
  }

  _lockRow(row, rowId) {
    this.lockedAssets.add(rowId)
    const lockButton = row.querySelector("[data-field='lock-button']")
    if (lockButton) {
      lockButton.classList.remove("text-gray-400", "dark:text-gray-500")
      lockButton.classList.add("text-primary-600", "dark:text-primary-400")
      const lockIcon = lockButton.querySelector(".lock-icon")
      const unlockIcon = lockButton.querySelector(".unlock-icon")
      if (lockIcon) lockIcon.classList.remove("hidden")
      if (unlockIcon) unlockIcon.classList.add("hidden")
      lockButton.title = "Unlock mark value"
    }
  }

  _unlockRow(row, rowId) {
    this.lockedAssets.delete(rowId)
    const lockButton = row.querySelector("[data-field='lock-button']")
    if (lockButton) {
      lockButton.classList.remove("text-primary-600", "dark:text-primary-400")
      lockButton.classList.add("text-gray-400", "dark:text-gray-500")
      const lockIcon = lockButton.querySelector(".lock-icon")
      const unlockIcon = lockButton.querySelector(".unlock-icon")
      if (lockIcon) lockIcon.classList.add("hidden")
      if (unlockIcon) unlockIcon.classList.remove("hidden")
      lockButton.title = "Lock mark value"
    }
    // Clear manually entered price so recalculate fills it automatically
    const markPriceInput = row.querySelector("[data-field='mark-price']")
    if (markPriceInput) {
      markPriceInput.value = ""
    }
  }

  // --- User manually edits the price/unit input -> auto-lock ---

  markPriceEdited(event) {
    const row = event.target.closest("tr")
    const rowId = row.dataset.rowId
    const value = parseFloat(event.target.value)

    if (value > 0) {
      // Auto-lock when user types a price
      if (!this.lockedAssets.has(rowId)) {
        this._lockRow(row, rowId)
      }
    } else {
      // Cleared the input -> unlock
      if (this.lockedAssets.has(rowId)) {
        this._unlockRow(row, rowId)
      }
    }

    this.recalculate()
  }

  // --- Helper: get the selected asset type for a row ---

  _getRowAssetType(row) {
    const select = row.querySelector("select[name*='[asset_type]']")
    return select ? select.value : ""
  }

  _isDollarDenominated(row) {
    const assetType = this._getRowAssetType(row)
    return DOLLAR_DENOMINATED_TYPES.has(assetType)
  }

  // --- Live mark calculation with type-aware distribution ---
  //
  // Dollar-denominated (SAFE, NOTE, CASH, ESCROW):
  //   value = quantity (in dollars), price/unit = $1.00
  //   These have a fixed implied value equal to their quantity.
  //
  // EQUITY (or no type selected):
  //   value = allocated capital, price/unit = value / shares
  //   Remaining capital after dollar-denominated assets is distributed
  //   among equity rows, with lock/unlock support.
  //   User can also type a price directly, which auto-locks the row.

  recalculate() {
    const investedCapital = parseFloat(this.investedCapitalTarget.value) || 0
    const rows = this.assetRowTargets

    // Gather info about each row
    const rowData = rows.map(row => {
      const quantityInput = row.querySelector("input[name*='[quantity]']")
      const markPriceInput = row.querySelector("[data-field='mark-price']")
      const markValueEl = row.querySelector("[data-field='mark-value']")
      const rowId = row.dataset.rowId
      const quantity = parseFloat(quantityInput?.value) || 0
      const isDollar = this._isDollarDenominated(row)
      const isLocked = !isDollar && this.lockedAssets.has(rowId)
      // Read manually entered price from the input
      const manualPrice = markPriceInput ? parseFloat(markPriceInput.value) : NaN

      return { row, quantityInput, markPriceInput, markValueEl, rowId, quantity, isDollar, isLocked, manualPrice }
    })

    // Phase 1: Sum dollar-denominated asset values (value = quantity in dollars)
    let dollarTotal = 0
    rowData.filter(d => d.isDollar && d.quantity > 0).forEach(d => {
      dollarTotal += d.quantity
    })

    // Phase 2: Sum locked equity values (from manually entered prices)
    let lockedEquityTotal = 0
    rowData.filter(d => d.isLocked && d.quantity > 0).forEach(d => {
      const price = !isNaN(d.manualPrice) && d.manualPrice > 0 ? d.manualPrice : this._getRowPrice(d.row)
      if (price > 0) {
        lockedEquityTotal += price * d.quantity
      }
    })

    // Phase 3: Calculate remaining for unlocked equity rows
    const unlockedEquityRows = rowData.filter(d => !d.isDollar && !d.isLocked && d.quantity > 0)
    const remainingForEquity = investedCapital - dollarTotal - lockedEquityTotal
    const unlockedEquityCount = unlockedEquityRows.length

    let totalImpliedValue = 0
    let activeAssetCount = 0

    rowData.forEach(d => {
      if (!d.markPriceInput) return

      if (d.quantity <= 0 || investedCapital <= 0) {
        this._showPlaceholder(d)
        return
      }

      let pricePerUnit
      let impliedValue

      if (d.isDollar) {
        // Dollar-denominated: value = quantity, price = $1.00
        pricePerUnit = 1.0
        impliedValue = d.quantity
        // Show fixed price in the disabled input
        d.markPriceInput.value = ""
        d.markPriceInput.placeholder = "$1.00"
      } else if (d.isLocked) {
        // Locked equity: use the manually entered price or stored price
        pricePerUnit = !isNaN(d.manualPrice) && d.manualPrice > 0 ? d.manualPrice : this._getRowPrice(d.row)
        if (pricePerUnit <= 0) {
          const available = investedCapital - dollarTotal
          pricePerUnit = available > 0 ? available / d.quantity : 0
        }
        impliedValue = pricePerUnit * d.quantity
        // Don't overwrite user-typed value - only set if empty
        if (isNaN(d.manualPrice) || d.manualPrice <= 0) {
          d.markPriceInput.value = pricePerUnit > 0 ? this._formatPriceRaw(pricePerUnit) : ""
        }
      } else if (unlockedEquityCount > 0 && remainingForEquity > 0) {
        // Unlocked equity: distribute remaining capital evenly
        const shareOfRemaining = remainingForEquity / unlockedEquityCount
        pricePerUnit = shareOfRemaining / d.quantity
        impliedValue = shareOfRemaining
        // Update the input with the auto-calculated price (user hasn't touched it)
        d.markPriceInput.value = this._formatPriceRaw(pricePerUnit)
      } else {
        // No remaining capital for equity
        pricePerUnit = 0
        impliedValue = 0
        d.markPriceInput.value = ""
      }

      if (d.markValueEl) {
        d.markValueEl.textContent = impliedValue > 0 ? `Value: ${formatCurrency(impliedValue)}` : ""
      }

      // Store current price for locked row retrieval
      d.row.dataset.currentPrice = pricePerUnit.toString()

      totalImpliedValue += impliedValue
      activeAssetCount++
    })

    // Update summary panel
    this.summaryInvestedTarget.textContent = investedCapital > 0 ? formatCurrency(investedCapital) : "-"
    this.summaryTotalValueTarget.textContent = totalImpliedValue > 0 ? formatCurrency(totalImpliedValue) : "-"
    this.summaryAssetCountTarget.textContent = activeAssetCount.toString()

    this._updateTotalStatus(investedCapital, totalImpliedValue, activeAssetCount)
  }

  _showPlaceholder(d) {
    if (d.markPriceInput) {
      d.markPriceInput.value = ""
      d.markPriceInput.placeholder = "Auto"
    }
    if (d.markValueEl) d.markValueEl.textContent = ""
  }

  // Read the stored price from a row's data attribute
  _getRowPrice(row) {
    return parseFloat(row.dataset.currentPrice) || 0
  }

  // Format a price as a plain number (for setting input value, not display)
  _formatPriceRaw(value) {
    if (value < 1) return value.toFixed(4)
    if (value < 100) return value.toFixed(4)
    return value.toFixed(2)
  }

  // Color-coded status for the total value vs invested capital
  // Also disables submit when total exceeds invested capital
  _updateTotalStatus(investedCapital, totalImpliedValue, activeAssetCount) {
    if (!this.hasSummaryTotalStatusTarget) return

    const statusEl = this.summaryTotalStatusTarget
    const totalEl = this.summaryTotalValueTarget

    if (activeAssetCount === 0 || investedCapital <= 0) {
      statusEl.textContent = ""
      statusEl.className = "ml-2 text-xs"
      totalEl.className = "text-lg font-semibold text-gray-900 dark:text-white"
      this._setSubmitEnabled(true)
      return
    }

    const tolerance = 0.01
    const diff = totalImpliedValue - investedCapital

    if (Math.abs(diff) < tolerance) {
      // Perfect match
      statusEl.textContent = "✓ Matches invested capital"
      statusEl.className = "ml-2 text-xs text-green-600 dark:text-green-400"
      totalEl.className = "text-lg font-semibold text-green-700 dark:text-green-400"
      this._setSubmitEnabled(true)
    } else if (totalImpliedValue < investedCapital) {
      // Under
      const remaining = investedCapital - totalImpliedValue
      statusEl.textContent = `↓ ${formatCurrency(remaining)} unallocated`
      statusEl.className = "ml-2 text-xs text-yellow-700 dark:text-yellow-400"
      totalEl.className = "text-lg font-semibold text-yellow-700 dark:text-yellow-400"
      this._setSubmitEnabled(true)
    } else {
      // Over — block submission
      const over = totalImpliedValue - investedCapital
      statusEl.textContent = `↑ ${formatCurrency(over)} over invested capital — reduce prices or add more capital`
      statusEl.className = "ml-2 text-xs text-red-700 dark:text-red-400"
      totalEl.className = "text-lg font-semibold text-red-700 dark:text-red-400"
      this._setSubmitEnabled(false)
    }
  }

  _setSubmitEnabled(enabled) {
    if (!this.hasSubmitButtonTarget) return
    const btn = this.submitButtonTarget
    btn.disabled = !enabled
    if (enabled) {
      btn.classList.remove("opacity-50", "cursor-not-allowed")
    } else {
      btn.classList.add("opacity-50", "cursor-not-allowed")
    }
  }

  // --- Dynamic asset rows ---

  _updateRemoveButtonVisibility() {
    const rowCount = this.assetRowTargets.length
    this.removeAssetButtonTargets.forEach(button => {
      if (rowCount <= 1) {
        button.classList.add("hidden")
      } else {
        button.classList.remove("hidden")
      }
    })
  }

  addAssetRow() {
    const tbody = this.assetsListTarget
    const newIndex = new Date().getTime()

    const row = document.createElement('tr')
    row.className = 'border-b dark:border-gray-700'
    row.setAttribute('data-position-form-target', 'assetRow')
    row.setAttribute('data-row-id', newIndex)

    row.innerHTML = `
      <td class="px-4 py-3">
        <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Type</label>
        <select name="position[assets_attributes][${newIndex}][asset_type]"
                class="portal-bg-subtle border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:border-gray-600 dark:text-white"
                data-action="change->position-form#assetTypeChanged"
                required>
          <option value="">Select type...</option>
          <option value="SAFE">SAFE</option>
          <option value="EQUITY">Equity</option>
          <option value="CASH">Cash</option>
          <option value="NOTE">Note</option>
          <option value="ESCROW">Escrow</option>
        </select>
      </td>
      <td class="px-4 py-3">
        <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1" data-field="quantity-label">Quantity</label>
        <input type="number" name="position[assets_attributes][${newIndex}][quantity]"
               step="0.01" min="0" placeholder="e.g., 100000"
               class="portal-bg-subtle border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:border-gray-600 dark:text-white"
               data-action="input->position-form#recalculate"
               required>
      </td>
      <td class="px-4 py-3" data-field="cap">
        <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Cap ($)</label>
        <input type="number" name="position[assets_attributes][${newIndex}][cap]"
               step="0.01" min="0" placeholder="e.g., 10000000"
               class="portal-bg-subtle border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:border-gray-600 dark:text-white">
      </td>
      <td class="px-4 py-3" data-field="discount">
        <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Discount (%)</label>
        <input type="number" name="position[assets_attributes][${newIndex}][discount]"
               step="1" min="0" max="100" placeholder="e.g., 20"
               class="portal-bg-subtle border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:border-gray-600 dark:text-white">
      </td>
      <td class="px-4 py-3" data-field="mark-preview">
        <div class="flex items-center space-x-2">
          <div class="flex-grow">
            <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">Price/Unit</div>
            <input type="number"
                   name="position[assets_attributes][${newIndex}][mark_price]"
                   step="0.0001" min="0"
                   placeholder="Auto"
                   class="portal-bg-subtle border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-1.5 dark:border-gray-600 dark:text-white"
                   data-field="mark-price"
                   data-action="input->position-form#markPriceEdited">
            <div class="text-xs text-gray-400 dark:text-gray-500 mt-1" data-field="mark-value"></div>
          </div>
          <button type="button"
                  class="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 flex-shrink-0"
                  data-action="click->position-form#toggleLock"
                  data-field="lock-button"
                  title="Lock mark value">
            <svg class="w-4 h-4 lock-icon hidden" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"></path>
            </svg>
            <svg class="w-4 h-4 unlock-icon" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z"></path>
            </svg>
          </button>
        </div>
      </td>
      <td class="px-4 py-3">
        <button type="button"
                class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                data-action="click->position-form#removeAssetRow"
                data-position-form-target="removeAssetButton">
          Remove
        </button>
      </td>
    `

    tbody.appendChild(row)
    this._updateRemoveButtonVisibility()
    this.recalculate()
  }

  removeAssetRow(event) {
    const row = event.target.closest('tr')
    const rowId = row.dataset.rowId

    // Remove from locked set if it was locked
    if (rowId && this.lockedAssets.has(rowId)) {
      this.lockedAssets.delete(rowId)
    }

    row.remove()
    this._updateRemoveButtonVisibility()
    this.recalculate()
  }

  // --- URL normalization ---
  // Auto-prepend https:// to URL fields if no protocol is present

  normalizeUrl(event) {
    const input = event.target
    const value = input.value.trim()

    if (value && !value.match(/^https?:\/\//i)) {
      input.value = `https://${value}`
    }
  }
}
